// users/{uid}/engineDeployments/{id} — the member's 실전 기록: occasions they
// used (썼어요) or encountered (들었어요) an English expression. ONE system:
// the review ceremony's 표현 예약하기 creates a PENDING record here, the home
// glance surfaces pendings, the composer completes them, the log keeps them.
// Private per member; the wall stays the only public engine surface.
//
// Doc shape (state DERIVED from sentence OR confirmation — see
// services/engineDeploy.js for the full field list incl. dueDate/dueTime,
// per-expression used flags, and the snooze/surfaced instrumentation).
import { db } from "@/firebaseConfig";
import { noteFailure } from "@/utils/silentFailure";
import { markEngineDirty } from "@/services/engineBus";
import { withDerivedStamps, guardSentence, guardConfirm } from "@/services/engineDeploy";
import engineEventsService from "@/api/engineEventsService";
import { dateKeyOf } from "@/services/engineScheduler";

const coll = (uid) => db.collection("users").doc(uid).collection("engineDeployments");

function logEvent(uid, rec, prev, meta = {}) {
  // deploy_reserved on a pending birth, deploy_logged on the completion
  // transition — the north-star counter rides the same writes it measures.
  // The ruled instruments: hasDate on reserves (does door ① work?), fromPrep
  // + the used/unused split on completions (prep→used conversion, and which
  // reserved expressions never got used).
  const now = Date.now();
  if (!prev && !rec.completedAt) {
    engineEventsService.log(uid, "deploy_reserved", {
      expressionUids: (rec.expressions || []).map((e) => e.uid),
      hasDate: !!(rec.dueDate || rec.dueWeek),
      hasWeek: !!(rec.dueWeek && !rec.dueDate), // 다음 주 (ruled 2026-09-04)
      scene: rec.scene || null,
      ...meta,
    }, dateKeyOf(now));
  } else if (rec.completedAt && !(prev && prev.completedAt)) {
    const exprs = rec.expressions || [];
    engineEventsService.log(uid, "deploy_logged", {
      usedUids: exprs.filter((e) => e.used !== false).map((e) => e.uid),
      unusedUids: exprs.filter((e) => e.used === false).map((e) => e.uid),
      kind: rec.kind || null,
      fromPrep: !!(prev && !prev.completedAt),
      hasSentence: !!String(rec.sentence || "").trim(),
      daysSinceCreate: prev && prev.createdAt
        ? Math.round((now - prev.createdAt) / 86400000) : 0,
      scene: rec.scene || null,
      ...meta,
    }, dateKeyOf(now));
  }
}

const firestoreImpl = {
  /** Every record, newest activity first. Fail-soft to an empty log. */
  async list(uid) {
    if (!uid) return [];
    try {
      const snap = await coll(uid).get();
      const rows = [];
      snap.forEach((d) => rows.push({ id: d.id, ...d.data() }));
      rows.sort((a, b) => (b.updatedAt || b.createdAt || 0) - (a.updatedAt || a.createdAt || 0));
      return rows;
    } catch (e) {
      noteFailure("engineDeployService.list", e);
      return [];
    }
  },
  /** rec: the record fields (no id). Returns the new id, or null. */
  async create(uid, rec, meta) {
    if (!uid || !rec) return null;
    try {
      const now = Date.now();
      const doc = withDerivedStamps({ ...rec, createdAt: now }, null, now);
      const ref = await coll(uid).add(doc);
      logEvent(uid, doc, null, meta);
      markEngineDirty();
      return ref.id;
    } catch (e) {
      noteFailure("engineDeployService.create", e);
      return null;
    }
  },
  /** Full-field update (every field is editable, ruled). The stamp and the
   *  deploy_logged gate derive from the FRESH server doc inside a
   *  transaction — a stale client copy must never revert a completion done
   *  on another device, move a keepsake's completedAt, or double-fire the
   *  north-star event. */
  async update(uid, id, rec, clientPrev, meta) {
    if (!uid || !id || !rec) return false;
    try {
      const ref = coll(uid).doc(id);
      let doc = null;
      let prev = null;
      await db.runTransaction(async (tx) => {
        const snap = await tx.get(ref);
        prev = snap.exists ? snap.data() : null;
        // a stale tab must never erase a completion it never saw —
        // neither the sentence nor the confirmation that completes it
        const merged = {
          ...rec,
          sentence: guardSentence(rec.sentence, clientPrev, prev),
          confirmedAt: guardConfirm(rec.confirmedAt, clientPrev, prev),
        };
        doc = withDerivedStamps(merged, prev, Date.now());
        delete doc.id;
        tx.set(ref, doc, { merge: false });
      });
      logEvent(uid, doc, prev, meta);
      markEngineDirty();
      return true;
    } catch (e) {
      noteFailure("engineDeployService.update", e);
      return false;
    }
  },
  /** The fourth door is a question, not a record — but it is a real-work
   *  moment the B2B corpus wants counted: which scenes members get stuck in. */
  noteStuck(uid, meta = {}) {
    if (!uid) return;
    engineEventsService.log(uid, "deploy_stuck", { ...meta }, dateKeyOf(Date.now()));
  },
  /** Their record, their right (long-press delete, ruled). */
  async remove(uid, id) {
    if (!uid || !id) return false;
    try {
      await coll(uid).doc(id).delete();
      markEngineDirty();
      return true;
    } catch (e) {
      noteFailure("engineDeployService.remove", e);
      return false;
    }
  },
};

// Same DEV-ONLY escape hatch as the other engine services: local mode keeps
// the log in this browser for Eddie's 8080 walks. Emulator mode outranks it.
let impl = firestoreImpl;
// NB: keyed on the FLAG alone, not NODE_ENV — a minified check build
// (VUE_APP_ENGINE_LOCAL=true npm run build) must keep local mode, or it
// boots against prod engine collections whose rules are deliberately not
// deployed and dies on 연결이 불안정해요 (seen live, phone, 2026-08-29).
// Real deploys never set the flag, so dead-code elimination there holds.
if (
  process.env.VUE_APP_ENGINE_LOCAL === "true"
  && process.env.VUE_APP_USE_EMULATOR !== "true"
) {
  const KEY = "engineDeployLocal.v1";
  const read = () => {
    try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch (e) { return []; }
  };
  const write = (rows) => localStorage.setItem(KEY, JSON.stringify(rows));
  impl = {
    async list() {
      return read().sort((a, b) => (b.updatedAt || b.createdAt || 0) - (a.updatedAt || a.createdAt || 0));
    },
    // NOTE: no logEvent here — the events service writes to LIVE Firestore,
    // and local mode's contract is that engine writes stay in this browser.
    async create(uid, rec, meta) { // eslint-disable-line no-unused-vars
      const now = Date.now();
      const doc = withDerivedStamps({ ...rec, createdAt: now }, null, now);
      const id = `local-${now}-${Math.floor(Math.random() * 1e6)}`;
      write([...read(), { ...doc, id }]);
      markEngineDirty();
      return id;
    },
    async update(uid, id, rec, clientPrev, meta) { // eslint-disable-line no-unused-vars
      const prev = read().find((r) => r.id === id) || null; // fresh, like the tx
      const merged = {
        ...rec,
        sentence: guardSentence(rec.sentence, clientPrev, prev),
        confirmedAt: guardConfirm(rec.confirmedAt, clientPrev, prev),
      };
      const doc = withDerivedStamps(merged, prev, Date.now());
      write(read().map((r) => (r.id === id ? { ...doc, id } : r)));
      markEngineDirty();
      return true;
    },
    noteStuck() {}, // local mode: engine writes stay in this browser, no live events
    async remove(uid, id) {
      write(read().filter((r) => r.id !== id));
      markEngineDirty();
      return true;
    },
  };
}

export default impl;
